import React, { useEffect, useRef } from "react";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/store";

// Fix default marker icons for bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const AU_CENTER = [-25.27, 133.77];

const linkHref = (kind, val) => {
  if (!val) return null;
  if (kind === "whatsapp") { if (val.startsWith("http")) return val; const d = val.replace(/[^0-9]/g, ""); return d ? `https://wa.me/${d}` : null; }
  const v = val.startsWith("http") ? val : `https://${val}`;
  return /^https?:\/\//i.test(v) ? v : null;
};

export default function MapView({ items, center }) {
  const elRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const navigate = useNavigate();
  const { t, lang } = useApp();

  useEffect(() => {
    if (!mapRef.current && elRef.current) {
      mapRef.current = L.map(elRef.current).setView(center || AU_CENTER, center ? 11 : 4);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors", maxZoom: 19,
      }).addTo(mapRef.current);
      layerRef.current = L.layerGroup().addTo(mapRef.current);
    }
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const pts = (items || []).filter((i) => i.lat != null && i.lng != null);
    pts.forEach((i) => {
      const m = L.marker([i.lat, i.lng]).addTo(layer);
      const div = document.createElement("div");
      const links = i.contact_links || {};
      const el = (tag, text, style, attrs = {}) => {
        const e = document.createElement(tag); e.textContent = text; e.style.cssText = style;
        Object.entries(attrs).forEach(([k, v]) => e.setAttribute(k, v)); return e;
      };
      const btn = (href, label, color, tid) => href ? el("a", label,
        `display:inline-block;margin:6px 6px 0 0;padding:4px 10px;border-radius:999px;background:${color};color:#fff;font-size:11px;font-weight:700;text-decoration:none`,
        { href, target: "_blank", rel: "noreferrer noopener", "data-testid": `${tid}-${i.listing_id}` }) : null;
      const shareUrl = `${window.location.origin}/listing/${i.listing_id}`;
      const shareHref = `https://wa.me/?text=${encodeURIComponent(`${t("share_text")} ${i.title} ${shareUrl}`)}`;
      div.appendChild(el("strong", i.title, "font-family:sans-serif"));
      if (i.owner_is_pro) div.appendChild(el("span", `♛ ${t("pro_business")}`, "display:inline-block;margin-left:4px;padding:1px 7px;border-radius:999px;background:#0A0B10;color:#fde047;font-size:10px;font-weight:700", { "data-testid": `map-pro-badge-${i.listing_id}` }));
      div.appendChild(document.createElement("br"));
      div.appendChild(el("span", i.city || "", "color:#666"));
      div.appendChild(document.createElement("br"));
      const view = el("a", t("map_view_listing"), "color:#002FA7;font-weight:700", { href: "#", "data-testid": `map-view-listing-${i.listing_id}` });
      view.addEventListener("click", (e) => { e.preventDefault(); navigate(`/listing/${i.listing_id}`); });
      div.appendChild(view);
      const contacts = el("div", "", "", { "data-testid": `map-contacts-${i.listing_id}` });
      [btn(linkHref("whatsapp", links.whatsapp), "WhatsApp", "#25D366", "map-contact-whatsapp"),
        btn(linkHref("website", links.website), "Website", "#002FA7", "map-contact-website"),
        btn(shareHref, `↗ ${t("map_share")}`, "#0A0B10", "map-share")].forEach((b) => b && contacts.appendChild(b));
      div.appendChild(contacts);
      m.bindPopup(div);
    });
    if (center) mapRef.current.setView(center, 11);
    else if (pts.length) mapRef.current.fitBounds(L.latLngBounds(pts.map((p) => [p.lat, p.lng])).pad(0.3), { maxZoom: 11 });
    else mapRef.current.setView(AU_CENTER, 4);
    setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 100);
  }, [items, center, navigate, lang]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  return <div ref={elRef} data-testid="map-view" style={{ height: "540px" }} className="relative z-0 overflow-hidden rounded-2xl border border-gray-200" />;
}
